import type { GenericArrayOfObjects } from '@/interfaces/api';
import type { INovaPersistedWarehouse, INovaWarehouse } from '@/interfaces/nova';
import type { NovaApiService } from '@/services/nova/NovaApiService';
import { removeInvalidCustomFieldsKeys } from '@/services/appointment_plus/APNovaMap';
import { objHasProperty } from '@/services/util';
import {
  ALREADY_EXISTS_MESSAGE,
  SUCCESS_IMPORT_MESSAGE,
  forEachSequentially,
  getFailureStatusMessage,
  parseJsonField,
  removeEmptyProperties,
  type IImportSession
} from './importShared';

type WarehouseImportParams = {
  warehouses: INovaWarehouse[];
  apiService: NovaApiService;
  existingWarehouses: INovaPersistedWarehouse[];
  session: IImportSession;
};

export async function updateWarehouseCustomFields(
  apiService: NovaApiService,
  warehouseId: string,
  customFields: GenericArrayOfObjects | string | null | undefined
) {
  let parsedCustomFields = customFields;
  if (typeof parsedCustomFields === 'string') {
    try {
      parsedCustomFields = JSON.parse(parsedCustomFields) as GenericArrayOfObjects;
    } catch {
      parsedCustomFields = [];
    }
  }
  const isEmpty = Array.isArray(parsedCustomFields) && parsedCustomFields.length < 1;
  await apiService.updateWarehouse(warehouseId, {
    customApptFieldsTemplate: isEmpty ? null : parsedCustomFields
  });
}

function stringifyWarehouseJsonFields(warehouse: INovaWarehouse) {
  warehouse.schedule = JSON.stringify(warehouse.schedule);
  warehouse.ppeRequirements = JSON.stringify(warehouse.ppeRequirements);
  warehouse.amenities = JSON.stringify(warehouse.amenities);
  if (warehouse.customApptFieldsTemplate) {
    warehouse.customApptFieldsTemplate = JSON.stringify(warehouse.customApptFieldsTemplate);
  }
}

async function importExistingWarehouse(
  warehouse: INovaWarehouse,
  existingWarehouse: INovaPersistedWarehouse,
  warehousesKeyedByExternalId: { [key: string]: INovaWarehouse },
  { apiService, session }: Pick<WarehouseImportParams, 'apiService' | 'session'>
) {
  warehouse.wasImportedIntoNova = ALREADY_EXISTS_MESSAGE;
  warehouse.id = existingWarehouse.id;
  if (!session.warehouseCustomFields) {
    session.warehouseCustomFields = {};
  }
  if (!objHasProperty(session.warehouseCustomFields, existingWarehouse.id)) {
    session.warehouseCustomFields[existingWarehouse.id] =
      existingWarehouse.customApptFieldsTemplate;
  }

  if (warehouse.externalId && existingWarehouse.id) {
    existingWarehouse.externalHeadquartersId = warehouse.externalHeadquartersId;
    warehousesKeyedByExternalId[warehouse.externalId] = existingWarehouse;
  }
  if (warehouse.customApptFieldsTemplate) {
    await updateWarehouseCustomFields(
      apiService,
      existingWarehouse.id,
      warehouse.customApptFieldsTemplate
    );
    warehouse.customApptFieldsTemplate = JSON.stringify(warehouse.customApptFieldsTemplate);
  }
  session.warehouseReport.push(warehouse);
}

async function createNewWarehouse(
  warehouse: INovaWarehouse,
  warehousesKeyedByExternalId: { [key: string]: INovaWarehouse },
  { apiService, session }: Pick<WarehouseImportParams, 'apiService' | 'session'>
) {
  warehouse.schedule = parseJsonField(warehouse.schedule);
  if (warehouse.ppeRequirements) {
    warehouse.ppeRequirements = parseJsonField(warehouse.ppeRequirements);
  }
  if (warehouse.amenities) {
    warehouse.amenities = parseJsonField(warehouse.amenities);
  }
  try {
    const response = await apiService.createWarehouse(warehouse);
    const [newWarehouse] = response.data;
    if (!newWarehouse) {
      throw new Error('no warehouse was returned');
    }
    warehouse.wasImportedIntoNova = SUCCESS_IMPORT_MESSAGE;
    warehouse.id = newWarehouse.id;
    newWarehouse.externalHeadquartersId = warehouse.externalHeadquartersId;
    if (warehouse.externalId && newWarehouse.id) {
      warehousesKeyedByExternalId[warehouse.externalId] = warehouse;
    }
    stringifyWarehouseJsonFields(warehouse);
    session.warehouseReport.push(warehouse);
  } catch (e) {
    stringifyWarehouseJsonFields(warehouse);
    warehouse.wasImportedIntoNova = getFailureStatusMessage({ e });
    session.warehouseReport.unshift(warehouse);
  }
}

export async function importWarehouses(params: WarehouseImportParams) {
  const { warehouses, existingWarehouses, session } = params;
  const warehousesKeyedByExternalId: { [key: string]: INovaWarehouse } = {};
  session.warehouseReport = [];

  await forEachSequentially(warehouses, async warehouse => {
    if (warehouse?.wasImportedIntoNova === SUCCESS_IMPORT_MESSAGE) {
      warehouse.wasImportedIntoNova = ALREADY_EXISTS_MESSAGE;
      session.warehouseReport.push(warehouse);
      warehousesKeyedByExternalId[warehouse.externalId] = warehouse;
      return;
    }
    // TODO: Seeing pattern with checking existing entities here - explore abstraction
    const existingWarehouse = existingWarehouses.find(existing => warehouse.name === existing.name);
    removeEmptyProperties(warehouse);
    if (warehouse.customApptFieldsTemplate) {
      warehouse.customApptFieldsTemplate = removeInvalidCustomFieldsKeys(
        JSON.parse(warehouse.customApptFieldsTemplate as string) as GenericArrayOfObjects
      );
    }
    if (existingWarehouse) {
      await importExistingWarehouse(
        warehouse,
        existingWarehouse,
        warehousesKeyedByExternalId,
        params
      );
      return;
    }
    await createNewWarehouse(warehouse, warehousesKeyedByExternalId, params);
  });
  return { warehousesKeyedByExternalId };
}
